import React, { useState } from "react"
import { KeyboardDatePicker, KeyboardTimePicker, MuiPickersUtilsProvider } from "@impelsys/material-ui-pickers"
import { AccessTime, Alarm, Schedule, Today } from "@material-ui/icons"
import { useTheme } from "@material-ui/styles"
import { useMediaQuery } from "@material-ui/core"
import MomentUtils from "@date-io/moment"
import moment from "moment-timezone"
import { useDateUtils } from "../../utils"
import { AssignerDisplay } from "../Assigners"

const DateTimeAssigner = ({
  dateLabel = "Date",
  timeLabel = "Time",
  dueLabel = "Due",
  timeDisplayPrefix = "",
  value,
  dueValue,
  onChange,
  onDueChange,
  emptyTimeLabel = "Nothing selected",
  emptyDueLabel = "Nothing selected",
  topBorder,
  hasDue = false,
  cy = "DateTimeAssigner",
}) => {
  const theme = useTheme()
  const xs = useMediaQuery(theme.breakpoints.down("xs"))
  const { hasTime, formatTime, scheduleDateToMoment, hasScheduleDate } = useDateUtils()

  const [openDate, setOpenDate] = useState(false)
  const [openTime, setOpenTime] = useState(false)
  const [openDue, setOpenDue] = useState(false)

  const momentValue = scheduleDateToMoment(value)

  const isToday = hasScheduleDate(value) && moment().isSame(moment(momentValue), "day")
  const isNow = hasScheduleDate(value) && moment().isSameOrAfter(momentValue)
  const isAllDay = hasScheduleDate(value) && momentValue.hours() + momentValue.minutes() === 0

  const handleDateChange = (date) => {
    if (hasTime(momentValue)) {
      onChange(
        (date ? moment(date) : moment())
          .startOf("day")
          .add(scheduleDateToMoment(value).hours(), "hours")
          .add(scheduleDateToMoment(value).minutes(), "minutes")
      )
    } else {
      onChange(date ? moment(date).startOf("day") : moment().startOf("day"))
    }
  }
  const handleTimeChange = (date) => {
    onChange(
      !date && hasScheduleDate(value)
        ? scheduleDateToMoment(value).startOf("day")
        : (hasScheduleDate(value) ? scheduleDateToMoment(value) : moment())
            .startOf("day")
            .add(moment(date).hours(), "hours")
            .add(moment(date).minutes(), "minutes")
    )
  }
  const handleDueChange = (date) => {
    const dateMoment = moment(date)
    const minutes = date && moment(date).diff(moment(date).startOf("day"), "minute")
    onDueChange &&
      onDueChange(
        date && moment(dateMoment.format("HH:mm"), "HH:mm").isBefore(moment(momentValue.format("HH:mm"), "HH:mm"))
          ? momentValue
          : date
          ? scheduleDateToMoment(value).startOf("day").add(minutes, "minutes")
          : null
      )
  }

  const handleDateClick = () => {
    setOpenDate(true)
  }
  const handleDateClose = () => {
    setOpenDate(false)
  }
  const handleTimeClick = () => {
    setOpenTime(true)
  }
  const handleTimeClose = () => {
    setOpenTime(false)
  }
  const handleDueClick = () => {
    setOpenDue(true)
  }
  const handleDueClose = () => {
    setOpenDue(false)
  }

  return (
    <MuiPickersUtilsProvider utils={MomentUtils}>
      <AssignerDisplay
        icon={<Today />}
        title={dateLabel}
        value={value && `${moment(momentValue).format(xs ? "DD/MMM/Y" : "Do MMMM Y")}${isToday ? " (Today)" : ""}`}
        onClick={handleDateClick}
        topBorder={topBorder}
        cy={`${cy}-display-date`}
      />
      <KeyboardDatePicker
        label={dateLabel}
        value={value}
        open={openDate}
        minutesStep={5}
        onChange={handleDateChange}
        onClick={handleDateClick}
        onClose={handleDateClose}
        clearable
        clearLabel="Today"
        showTodayButton={false}
        TextFieldComponent={() => null}
        fullWidth
        okLabel=""
        autoOk
      />
      <AssignerDisplay
        icon={<Schedule />}
        title={timeLabel}
        value={
          isAllDay
            ? `${emptyTimeLabel}${isToday && isNow ? " (Now)" : ""}`
            : value
            ? `${timeDisplayPrefix}${moment(momentValue).format("h:mmA")}${isToday && isNow ? " (Now)" : ""}`
            : null
        }
        onClick={handleTimeClick}
        topBorder={topBorder}
        cy={`${cy}-display-time`}
      />
      <KeyboardTimePicker
        label={timeLabel}
        value={value ? scheduleDateToMoment(value) : null}
        open={openTime}
        minutesStep={5}
        onChange={handleTimeChange}
        onClose={handleTimeClose}
        clearable
        clearLabel="Available all day"
        keyboardIcon={<AccessTime />}
        TextFieldComponent={() => null}
        emptyLabel={value && emptyTimeLabel}
      />
      {hasDue && (
        <>
          <AssignerDisplay
            icon={<Alarm />}
            title={dueLabel}
            value={hasScheduleDate(dueValue) ? formatTime(scheduleDateToMoment(dueValue)) : ""}
            onClick={handleDueClick}
            placeholder={emptyDueLabel}
            topBorder={topBorder}
            disabled={!value}
            cy={`${cy}-display-due`}
          />
          <KeyboardTimePicker
            label={dueLabel}
            value={dueValue ? scheduleDateToMoment(dueValue) : null}
            open={openDue}
            minutesStep={5}
            onChange={handleDueChange}
            onClose={handleDueClose}
            clearable
            clearLabel="None"
            keyboardIcon={<Alarm />}
            TextFieldComponent={() => null}
            emptyLabel={emptyDueLabel}
            minDate={value}
            disabled={!value}
          />
        </>
      )}
    </MuiPickersUtilsProvider>
  )
}

export { DateTimeAssigner }
